import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Grid,
  Box,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Button,
  Chip,
  Avatar,
  CircularProgress
} from "@mui/material";
import {
  Close as CloseIcon,
  School as SchoolIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  LocalHospital as LocalHospitalIcon,
  TrendingUp as TrendingUpIcon,
  Search as SearchIcon
} from "@mui/icons-material";
import coursesService from "../../../services/CoursesService";

const UserHistory = ({ open, onClose, selectedUser }) => {
  const [userData, setUserData] = useState({
    nombre: "",
    cedula: "",
    profile: "",
  });

  const [coursesData, setCoursesData] = useState({
    courses: [],
    totalCourses: 0,
    overallProgress: 0
  });

  const [loading, setLoading] = useState(false);
  const [filterInProgress, setFilterInProgress] = useState("");
  const [filterCompleted, setFilterCompleted] = useState("");

  // Función para limpiar HTML tags del título
  const stripHtmlTags = (html) => {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  // Obtener datos del usuario desde localStorage
  const getUsuarioConCedula = () => {
    try {
      const usuarioConCedula = localStorage.getItem('usuarioConCedula');
      return usuarioConCedula ? JSON.parse(usuarioConCedula) : null;
    } catch (error) {
      console.error('Error al obtener usuario del localStorage:', error);
      return null;
    }
  };

  // Cargar datos de cursos del usuario
  const loadUserCourses = async () => {
    const usuarioConCedula = getUsuarioConCedula();
    if (!usuarioConCedula?.id) {
      console.error('No se pudo obtener el ID del usuario');
      return;
    }

    setLoading(true);
    try {
      const data = await coursesService.getCoursesProgressByUser(usuarioConCedula.id);
      setCoursesData(data);
    } catch (error) {
      console.error('Error al cargar los cursos del usuario:', error);
    } finally {
      setLoading(false);
    }
  };

  // Separar cursos en progreso y completados
  const getCoursesInProgress = () => {
    return coursesData.courses.filter(course => !course.isCompleted);
  };

  const getCompletedCourses = () => {
    return coursesData.courses.filter(course => course.isCompleted);
  };

  const filteredInProgress = getCoursesInProgress().filter((curso) =>
    stripHtmlTags(curso.title).toLowerCase().includes(filterInProgress.toLowerCase())
  );

  const filteredCompleted = getCompletedCourses().filter((curso) =>
    stripHtmlTags(curso.title).toLowerCase().includes(filterCompleted.toLowerCase())
  );

  const getProgressColor = (progress) => {
    if (progress >= 80) return "#2563eb";
    if (progress >= 60) return "#3b82f6";
    if (progress >= 40) return "#60a5fa";
    return "#93c5fd";
  };

  const getGradeColor = (grade) => {
    if (grade >= 90) return "success";
    if (grade >= 80) return "info";
    if (grade >= 70) return "warning";
    return "default";
  };

  const averageGrade = () => {
    const completedCourses = getCompletedCourses();
    if (completedCourses.length === 0) return 0;
    
    const totalGrades = completedCourses.reduce((sum, curso) => sum + curso.progressPercentage, 0);
    return Math.round(totalGrades / completedCourses.length);
  };

  useEffect(() => {
    if (selectedUser) {
      setUserData({
        nombre: selectedUser.name || "Usuario",
        cedula: selectedUser.cedula || "",
        profile: selectedUser.label?.name || "Usuario",
      });
    }
  }, [selectedUser]);

  useEffect(() => {
    if (open) {
      loadUserCourses();
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        style: {
          borderRadius: 12,
          backgroundColor: '#ffffff'
        }
      }}
    >
      <DialogTitle sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        bgcolor: '#20b2aa',
        color: 'white',
        py: 2.5
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LocalHospitalIcon sx={{ fontSize: 24 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Historial del Usuario
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, bgcolor: '#f9fafb' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Información Personal */}
            <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
                <Avatar sx={{
                  width: 50,
                  height: 50,
                  bgcolor: '#1f2937',
                  fontSize: 18,
                  fontWeight: 600
                }}>
                  {userData.nombre.split(' ').map(n => n[0]).join('').toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937' }}>
                    {userData.nombre}
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#6b7280', fontWeight: 500 }}>
                    {userData.profile}
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ color: '#6b7280', mb: 0.5, fontWeight: 500 }}>
                    Número de Cédula
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#1f2937' }}>
                    {userData.cedula}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ color: '#6b7280', mb: 0.5, fontWeight: 500 }}>
                    Especialidad
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#1f2937' }}>
                    {userData.profile}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Resumen Académico */}
            <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
                Resumen Académico
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Box textAlign="center">
                    <SchoolIcon sx={{ fontSize: 24, color: '#1f2937', mb: 1 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2937' }}>
                      {coursesData.totalCourses}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      Total
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box textAlign="center">
                    <ScheduleIcon sx={{ fontSize: 24, color: '#6b7280', mb: 1 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#6b7280' }}>
                      {getCoursesInProgress().length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      En Progreso
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box textAlign="center">
                    <CheckCircleIcon sx={{ fontSize: 24, color: '#16a34a', mb: 1 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#16a34a' }}>
                      {getCompletedCourses().length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      Completados
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box textAlign="center">
                    <TrendingUpIcon sx={{ fontSize: 24, color: '#2563eb', mb: 1 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#2563eb' }}>
                      {coursesData.overallProgress}%
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      Progreso General
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Cursos en Progreso */}
            <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
                Cursos en Progreso
              </Typography>

              <TextField
                fullWidth
                variant="outlined"
                placeholder="Buscar cursos..."
                size="small"
                sx={{ mb: 2 }}
                value={filterInProgress}
                onChange={(e) => setFilterInProgress(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: '#9ca3af', mr: 1 }} />
                }}
              />

              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e5e7eb' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f9fafb' }}>
                      <TableCell>Curso</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Progreso</TableCell>
                      <TableCell>Videos</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredInProgress.length > 0 ? (
                      filteredInProgress.map((curso) => (
                        <TableRow key={curso.courseId}>
                          <TableCell>{stripHtmlTags(curso.title)}</TableCell>
                          <TableCell>
                            <Chip 
                              label={curso.status === 1 ? "Activo" : "Inactivo"} 
                              size="small" 
                              color={curso.status === 1 ? "success" : "default"}
                            />
                          </TableCell>
                          <TableCell sx={{ width: 150 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={curso.progressPercentage}
                                sx={{
                                  height: 8,
                                  borderRadius: 4,
                                  flex: 1,
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor: getProgressColor(curso.progressPercentage)
                                  }
                                }}
                              />
                              <Typography variant="body2">{curso.progressPercentage}%</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {curso.videoProgress.watchedVideos}/{curso.videoProgress.totalVideos} videos
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            No hay cursos en progreso
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* Cursos Finalizados */}
            <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
                Cursos Completados
              </Typography>

              <TextField
                fullWidth
                variant="outlined"
                placeholder="Buscar cursos..."
                size="small"
                sx={{ mb: 2 }}
                value={filterCompleted}
                onChange={(e) => setFilterCompleted(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: '#9ca3af', mr: 1 }} />
                }}
              />

              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e5e7eb' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f9fafb' }}>
                      <TableCell>Curso</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Calificación</TableCell>
                      <TableCell>Fecha Completado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCompleted.length > 0 ? (
                      filteredCompleted.map((curso) => (
                        <TableRow key={curso.courseId}>
                          <TableCell>{stripHtmlTags(curso.title)}</TableCell>
                          <TableCell>
                            <Chip 
                              label={curso.status === 1 ? "Activo" : "Inactivo"} 
                              size="small" 
                              color={curso.status === 1 ? "success" : "default"}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`${curso.progressPercentage}%`}
                              color={getGradeColor(curso.progressPercentage)}
                            />
                          </TableCell>
                          <TableCell>
                            {curso.completedAt ? new Date(curso.completedAt).toLocaleDateString() : 'N/A'}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            No hay cursos completados
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
          <Button variant="outlined" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={onClose}>
            Aceptar
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default UserHistory;
